import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SimpleGrid, Skeleton } from "@mantine/core";
import Indicator from "@/components/atoms/Indicator";
import { useGetCameraImagesWithDetections, useGetWasteStreamsByContractId } from "@/services/CameraControll";
import { ImageWithDetection } from "@/types";
import Image from "@/components/molecules/Image";
import Select from "@/components/atoms/Select";
import dayjs from "dayjs";
import Stack from "@/components/atoms/Stack";
import Group from "@/components/atoms/Group";
import Pagination from "@/components/molecules/Pagination";
import Text from "@/components/atoms/Text";
import Tooltip from "@/components/atoms/Tooltip";

const DEFAULT_PAGE_SIZE = 20;

interface Props {
  contractId: number;
  companyId: number;
}

export default function WasteStream({ contractId, companyId: _ }: Props) {
  const { t } = useTranslation();
  const [activePage, setActivePage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [cameraFilter, setCameraFilter] = useState<string | null>(null);

  const { data: wasteStreams, isLoading: isLoadingWasteStreams } = useGetWasteStreamsByContractId(contractId);
  const { data: images, isLoading: isLoadingImages } = useGetCameraImagesWithDetections(contractId);

  const wasteStreamOptions = useMemo(
    () =>
      (wasteStreams ?? [])
        .filter((x) => x.active)
        .map((ws) => ({
          value: String(ws.cameraId),
          label: `${ws.wasteStreamType} (Camera: ${ws.cameraId})`,
        })),
    [wasteStreams]
  );

  const allImages: ImageWithDetection[] = useMemo(() => {
    const raw = images ?? [];
    return cameraFilter ? raw.filter((img) => img.cameraId === Number(cameraFilter)) : raw;
  }, [images, cameraFilter]);

  const paginatedImages = useMemo(() => {
    const start = (activePage - 1) * rowsPerPage;
    return allImages.slice(start, start + rowsPerPage);
  }, [allImages, activePage, rowsPerPage]);

  return (
    <Stack gap="md" mt={10}>
      <Group justify="space-between" align="center">
        <Select
          data={wasteStreamOptions}
          label={"Filter by waste stream"}
          value={cameraFilter}
          onChange={(v) => {
            setCameraFilter(v);
            setActivePage(1);
          }}
          clearable
          w={180}
        />
      </Group>

      {isLoadingImages || isLoadingWasteStreams ? (
        <SimpleGrid cols={5} spacing="sm">
          {Array.from({ length: DEFAULT_PAGE_SIZE }).map((_, i) => (
            <Skeleton key={i} height={100} radius="sm" />
          ))}
        </SimpleGrid>
      ) : paginatedImages.length === 0 ? (
        <Text c="dimmed" ta="center" py="xl">
          {t("common:noImagesFound")}
        </Text>
      ) : (
        <SimpleGrid cols={5} spacing="sm">
          {paginatedImages.map((img) => (
            <Stack key={img.imageId} gap={4}>
              <Tooltip
                label={
                  <Stack gap={4}>
                    <Text fw={500}>{t("wasteStream:objectsNotMatchingWasteStream")}</Text>
                    {Object.entries(
                      img.wrongObjects.reduce<Record<string, number>>((acc, obj) => {
                        acc[obj] = (acc[obj] ?? 0) + 1;
                        return acc;
                      }, {})
                    ).map(([obj, count]) => (
                      <Text key={obj} size="xs">
                        {obj} {count > 1 ? `(${count})` : ""}
                      </Text>
                    ))}
                  </Stack>
                }
                withArrow
                multiline
                w={220}
                disabled={img.wrongObjects.length === 0}
              >
                <Indicator
                  color="orange"
                  label={img.wrongObjects.length}
                  size={20}
                  disabled={img.wrongObjects.length === 0}
                  inline
                >
                  <Image
                    src={`data:image/jpeg;base64,${img.annotatedImageBase64}`}
                    radius="sm"
                    fit="cover"
                    mah={200}
                    withFullscreen
                  />
                </Indicator>
              </Tooltip>
              <Text size="xs" c="dimmed">
                {dayjs(img.timestamp).format("HH:mm - DD.MM.YYYY")}
              </Text>
            </Stack>
          ))}
        </SimpleGrid>
      )}

      {allImages.length > 0 && (
        <Pagination
          totalItems={allImages.length}
          rowsPerPage={rowsPerPage}
          activePage={activePage}
          onRowsPerPageChange={(v) => {
            setRowsPerPage(Number(v ?? DEFAULT_PAGE_SIZE));
            setActivePage(1);
          }}
          onPageChange={setActivePage}
        />
      )}
    </Stack>
  );
}
